<template>
  <ehc-page>
    <ehc-header
      text="Guest Data"
      :actions="headerActions"
      @download="download.show = true"
    ></ehc-header>

    <!-- Guest count display -->
    <ehc-sheet class="mb-2">
      <div class="ml-4 mt-2 mb-2">
        <span class="text-h6">{{ guestCount }} guests found</span>
      </div>
      <div v-if="userHasPermission(['superAdmin'])" class="ml-4 mb-3">
        <v-text-field
          v-model="propertyNameSearch"
          label="Search by property name"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          @input="debouncedPropertySearch"
          class="mr-4"
          style="max-width: 300px"
        ></v-text-field>
      </div>
    </ehc-sheet>

    <!-- Superadmin switch to view all properties -->
    <ehc-sheet v-if="userHasPermission(['superAdmin'])" class="mb-2">
      <v-switch
        v-model="viewAllProperties"
        :label="
          viewAllProperties
            ? 'Viewing all properties'
            : 'Viewing my properties only'
        "
        color="primary"
        @change="onToggleViewAllProperties"
        class="ml-4 mt-2"
      ></v-switch>
    </ehc-sheet>

    <ehc-sheet>
      <ehc-table :headers="headers" :items="guestsFiltered"> </ehc-table>
    </ehc-sheet>

    <ehc-download-dialog
      v-model="download.show"
      :fetch="getData"
      :data="guestsFiltered"
    >
    </ehc-download-dialog>
  </ehc-page>
</template>

<script>
import auth from "@/mixins/auth.vue";
import mixins from "@/mixins";
import EhcDownloadDialog from "@/components/ehc-download-dialog.vue";

export default {
  mixins: [auth, mixins],
  components: { EhcDownloadDialog },
  data() {
    return {
      pageTitle: "Guest Data",
      search: "",
      propertyNameSearch: "",
      propertyNameSearchDebounced: "",
      viewAllProperties: false,
      download: {
        show: false,
      },
      headers: [
        {
          text: "Last Name",
          align: "start",
          sortable: true,
          value: "lname",
          filterable: true,
        },
        {
          text: "First Name",
          align: "start",
          sortable: true,
          value: "fname",
          filterable: true,
        },
        {
          text: "Email",
          align: "start",
          sortable: true,
          value: "email",
          filterable: true,
        },
        {
          text: "Phone",
          align: "start",
          sortable: false,
          value: "phone",
        },
        {
          text: "City",
          align: "start",
          sortable: true,
          value: "propertyCity",
          filterable: true,
        },
        {
          text: "State",
          align: "start",
          sortable: true,
          value: "propertyState",
          filterable: true,
        },
        {
          text: "Property",
          align: "start",
          sortable: true,
          value: "propertyName",
          filterable: true,
        },
        {
          text: "Created",
          value: "createdAtAsString",
          sortby: "createdAt",
          sortable: true,
        },
      ],
    };
  },
  methods: {
    debouncedPropertySearch: function () {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.propertyNameSearchDebounced = this.propertyNameSearch;
      }, 300);
    },
    onToggleViewAllProperties() {
      if (this.viewAllProperties) {
        // Get all guests from all properties
        this.$store
          .dispatch("getAllGuests")
          .then(() => console.log("loaded all guests"));
      } else {
        // Get only current user's guests
        this.$store
          .dispatch("getGuests", this.$store.getters.user.uid)
          .then(() => console.log("loaded user guests"));
      }
    },
    async getData(settings) {
      console.log("getData", settings);
      let mappedGuests = [];
      let guest = {};
      let headers = this.headers;
      this.guestsFiltered.forEach((el) => {
        guest = {};
        for (const header of headers) {
          //use the header to map the data
          guest[header.text] = el[header.value] || "";
        }
        mappedGuests.push(guest);
      });

      return mappedGuests;
    },
  },
  computed: {
    searchString: function () {
      return this.$store.getters.searchString;
    },
    headerActions() {
      let actions = [
        {
          actionType: "v-btn",
          label: "Download",
          data: this.guestsForDownload,
          fileName: "'MY_GUESTS'",
          header: "'EVERHOST GUEST DATA'",
          color: "primary",
          dark: true,
          prependIconURL: require("@/assets/icons/Download All@3x.svg"),
          clickEmit: "download",
        },
      ];
      return actions;
    },
    guests: function () {
      return this.$store.getters.guests
        .map((el) => {
          el.createdAtAsString = this.dateFormat(el.createdAt, "dateOnly");
          el.searchAble =
            el.lname +
            " " +
            el.fname +
            " " +
            el.email +
            " " +
            el.phone +
            " " +
            el.propertyCity +
            " " +
            el.propertyState +
            " " +
            el.propertyName;
          return el;
        })
        .sort((a, b) => {
          // Sort by createdAt descending (newest first)
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    },
    guestsFiltered: function () {
      let filtered = this.guests;

      // Filter by global search string
      if (this.gcvSearchString && this.gcvSearchString !== "") {
        filtered = filtered.filter((el) => {
          return el.searchAble
            .toLowerCase()
            .includes(this.gcvSearchString.toLowerCase());
        });
      }

      // Filter by property name search
      if (
        this.propertyNameSearchDebounced &&
        this.propertyNameSearchDebounced !== ""
      ) {
        filtered = filtered.filter((el) => {
          return (
            el.propertyName &&
            el.propertyName
              .toLowerCase()
              .includes(this.propertyNameSearchDebounced.toLowerCase())
          );
        });
      }

      return filtered;
    },

    guestsForDownload: function () {
      let guestsForDownload = [];
      let guest = {};
      this.guests.forEach((el) => {
        guest = {};
        guest.LAST_NAME = el.lname || "";
        guest.FIRST_NAME = el.fname || "";
        guest.EMAIL = el.email || "";
        guest.PHONE = el.phone || "";
        guest.CITY = el.propertyCity || "";
        guest.STATE = el.propertyState || "";
        guest.PROPERTY = el.propertyName || "";
        guest.PROPERTY_ID = el.propertyId || "";
        guest.CREATED = el.createdAtAsString || "";
        guestsForDownload.push(guest);
      });
      return guestsForDownload;
    },
    searchString: function () {
      return this.$store.getters.searchString;
    }, // this is the search string from the store
    guestCount: function () {
      return this.guestsFiltered.length;
    },
  },
  created() {
    // Load guests based on user permission and toggle state
    if (this.userHasPermission(["superAdmin"]) && this.viewAllProperties) {
      this.$store
        .dispatch("getAllGuests")
        .then(() => console.log("loaded all guests for superadmin"));
    } else {
      this.$store
        .dispatch("getGuests", this.$store.getters.user.uid)
        .then(() => console.log("loaded user guests"));
    }
  },
};
</script>
